/**
 * StepCreateOrg — First onboarding step: create organization.
 *
 * Collects org name + slug, calls POST /api/onboarding/org.
 */
import { useState, type FormEvent } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";

interface StepCreateOrgProps {
  onNext(orgId: string): void;
}

export function StepCreateOrg({ onNext }: StepCreateOrgProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleNameChange(value: string) {
    setName(value);
    // Auto-generate slug from name
    setSlug(
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.post<{ orgId: string }>("/api/onboarding/org", {
        name: name.trim(),
        slug: slug.trim(),
      });
      if (!result?.orgId) {
        throw new Error("Invalid response from server");
      }
      onNext(result.orgId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create organization",
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Create Your Organization
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your organization is where your team, agents, and channels live.
        </p>
      </div>

      <Input
        label="Organization Name"
        placeholder="Acme Corp"
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        required
      />

      <Input
        label="Slug"
        placeholder="acme-corp"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        required
      />
      <p className="text-xs text-muted-foreground -mt-4">
        Used in URLs: <span className="text-white/60">{slug || "your-slug"}.ajnt.ai</span>
      </p>

      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" className="w-full" loading={isLoading}>
        Create Organization
      </Button>
    </form>
  );
}
